import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Bar, BarChart, Line, ComposedChart, XAxis, YAxis, CartesianGrid } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../ui/chart';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { AXIS } from './chartTheme';
import { tipFormatter } from './ChartKit';

/**
 * Reportes → Personal (Fase 1): costo de personal mes a mes por tipo, comisiones
 * generado vs pagado, por empleado y volumen del área que comisiona.
 * Ventana fija de últimos 12 meses (no depende del PeriodPicker global).
 */

const money = (n: number) => {
  if (n == null) return '—';
  const s = n < 0 ? '-' : '';
  const a = Math.abs(n);
  if (a >= 1e6) return `${s}$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${s}$${Math.round(a / 1e3)}k`;
  return `${s}$${Math.round(a).toLocaleString('es-AR')}`;
};
const fullARS = (n: number) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n || 0);

const TIPO_LABEL: Record<string, string> = { sueldo: 'Sueldo', comision: 'Comisión', anticipo: 'Anticipo', bonus: 'Bonus', otro: 'Otro' };

export default function PersonalSection() {
  const [d, setD] = useState<any>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setError(''); setLoading(true);
    axios.get('/api/reports/tablero/personal')
      .then((r) => setD(r.data))
      .catch((e) => setError(e.response?.data?.error || 'Error cargando el reporte de personal'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-10 text-center text-muted-foreground">Cargando...</div>;
  if (error) return <div className="py-10 text-center text-red-600">{error}</div>;
  if (!d) return null;

  const k = d.kpis || {};

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Costo del mes</p>
          <p className="text-2xl font-semibold leading-none">{money(k.totalMesActual || 0)}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Promedio mensual</p>
          <p className="text-2xl font-semibold leading-none">{money(k.promedioMensual || 0)}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Variable (comisión + bonus)</p>
          <p className="text-2xl font-semibold leading-none">{k.pctVariable || 0}%</p>
          <span className="text-xs text-muted-foreground">del total del período</span>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Comisiones pendientes</p>
          <p className={`text-2xl font-semibold leading-none ${d.pendienteComisiones > 0 ? 'text-amber-600' : ''}`}>{money(d.pendienteComisiones || 0)}</p>
          <span className="text-xs text-muted-foreground">sin pagar</span>
        </CardContent></Card>
      </div>

      {/* Costo de personal mes a mes (apilado por tipo) */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
            <h3 className="font-semibold text-sm">Costo de personal · mes a mes</h3>
            <div className="flex gap-3 text-xs text-muted-foreground flex-wrap">
              {['sueldo', 'comision', 'anticipo', 'bonus', 'otro'].map((t, i) => (
                <span key={t} className="flex items-center gap-1"><span className="inline-block w-3 h-2 rounded-sm" style={{ background: `hsl(var(--chart-${i + 1}))` }} />{TIPO_LABEL[t]}</span>
              ))}
            </div>
          </div>
          <ChartContainer
            config={{
              sueldo: { label: 'Sueldo', color: 'hsl(var(--chart-1))' },
              comision: { label: 'Comisión', color: 'hsl(var(--chart-2))' },
              anticipo: { label: 'Anticipo', color: 'hsl(var(--chart-3))' },
              bonus: { label: 'Bonus', color: 'hsl(var(--chart-4))' },
              otro: { label: 'Otro', color: 'hsl(var(--chart-5))' },
            }}
            className="h-[260px] w-full"
          >
            <BarChart data={d.costoMensual} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} vertical={false} />
              <XAxis dataKey="label" {...AXIS} />
              <YAxis {...AXIS} tickFormatter={(v: number) => money(v)} width={56} />
              <ChartTooltip content={<ChartTooltipContent formatter={tipFormatter(TIPO_LABEL, money)} />} />
              <Bar dataKey="sueldo" stackId="a" fill="var(--color-sueldo)" maxBarSize={40} />
              <Bar dataKey="comision" stackId="a" fill="var(--color-comision)" maxBarSize={40} />
              <Bar dataKey="anticipo" stackId="a" fill="var(--color-anticipo)" maxBarSize={40} />
              <Bar dataKey="bonus" stackId="a" fill="var(--color-bonus)" maxBarSize={40} />
              <Bar dataKey="otro" stackId="a" fill="var(--color-otro)" radius={[3, 3, 0, 0]} maxBarSize={40} />
            </BarChart>
          </ChartContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Comisiones generado vs pagado */}
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
              <h3 className="font-semibold text-sm">Comisiones · generado vs pagado</h3>
              <div className="flex gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-2 rounded-sm" style={{ background: 'hsl(var(--chart-2))' }} />Generado</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 rounded" style={{ background: 'hsl(var(--chart-1))' }} />Pagado</span>
              </div>
            </div>
            <ChartContainer
              config={{ generado: { label: 'Generado', color: 'hsl(var(--chart-2))' }, pagado: { label: 'Pagado', color: 'hsl(var(--chart-1))' } }}
              className="h-[220px] w-full"
            >
              <ComposedChart data={d.comisionesMensual} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} vertical={false} />
                <XAxis dataKey="label" {...AXIS} />
                <YAxis {...AXIS} tickFormatter={(v: number) => money(v)} width={56} />
                <ChartTooltip content={<ChartTooltipContent formatter={tipFormatter({ generado: 'Generado', pagado: 'Pagado' }, money)} />} />
                <Bar dataKey="generado" fill="var(--color-generado)" radius={[3, 3, 0, 0]} maxBarSize={32} />
                <Line type="monotone" dataKey="pagado" stroke="var(--color-pagado)" strokeWidth={2} dot={{ r: 2 }} />
              </ComposedChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Volumen del área que comisiona */}
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
              <h3 className="font-semibold text-sm">Servicios que comisionan · por mes</h3>
              <span className="text-xs text-muted-foreground">cantidad de trabajos</span>
            </div>
            <ChartContainer config={{ cantidad: { label: 'Cantidad', color: 'hsl(var(--chart-3))' } }} className="h-[220px] w-full">
              <BarChart data={d.volumenMensual} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} vertical={false} />
                <XAxis dataKey="label" {...AXIS} />
                <YAxis {...AXIS} width={30} allowDecimals={false} />
                <ChartTooltip content={<ChartTooltipContent formatter={tipFormatter({ cantidad: 'Trabajos' }, (v: number) => String(Math.round(v)))} />} />
                <Bar dataKey="cantidad" fill="var(--color-cantidad)" radius={[3, 3, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Por empleado */}
      <Card>
        <CardContent className="pt-5">
          <h3 className="font-semibold text-sm mb-3">Por empleado · período</h3>
          {(!d.porEmpleado || d.porEmpleado.length === 0) ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Sin pagos registrados en el período.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b">
                  <TableHead className="h-9 px-2 text-xs">Empleado</TableHead>
                  <TableHead className="h-9 px-2 text-xs text-right">Sueldo</TableHead>
                  <TableHead className="h-9 px-2 text-xs text-right">Comisión</TableHead>
                  <TableHead className="h-9 px-2 text-xs text-right">Anticipo</TableHead>
                  <TableHead className="h-9 px-2 text-xs text-right">Bonus</TableHead>
                  <TableHead className="h-9 px-2 text-xs text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {d.porEmpleado.map((e: any) => (
                  <TableRow key={e.id} className="border-b hover:bg-muted/30">
                    <TableCell className="px-2 py-2 text-sm font-medium">{e.empleado}</TableCell>
                    <TableCell className="px-2 py-2 text-sm text-right text-muted-foreground">{fullARS(e.sueldo)}</TableCell>
                    <TableCell className="px-2 py-2 text-sm text-right text-muted-foreground">{fullARS(e.comision)}</TableCell>
                    <TableCell className="px-2 py-2 text-sm text-right text-muted-foreground">{fullARS(e.anticipo)}</TableCell>
                    <TableCell className="px-2 py-2 text-sm text-right text-muted-foreground">{fullARS(e.bonus + e.otro)}</TableCell>
                    <TableCell className="px-2 py-2 text-sm text-right font-semibold">{fullARS(e.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground px-1">
        Últimos 12 meses. Las comisiones se agrupan por cuándo se generaron; el histórico cargado de una vez puede concentrarse en un mes.
      </p>
    </div>
  );
}
